/** SUNTRONIX ONE — Executive Command Centre (Dashboard v3).
 *
 *  Route:  /  (Suntronix internal)
 *  Consumers: management, business owners, senior operations, admins.
 *
 *  Design principles: SEE → UNDERSTAND → ACT.
 *  Every tile drills down.  No fabricated data.  Role-aware ordering.
 *  Reuses the executive, commercial pipeline, and sales-desk aggregates
 *  already in the platform — no duplicate source-of-truth.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DASH } from "@/constants/testIds";
import {
  Rocket, Undo2, UserPlus, UserMinus, Wrench, Plus, ShieldCheck,
  ArrowRight, CheckCircle2, AlertTriangle, Sparkles, Activity,
  Warehouse, Truck, Receipt, ChevronRight, Zap, Package, Users,
  MessageSquare, ClipboardList, Recycle, Search as SearchIcon, Gift,
} from "lucide-react";
import { isCustomerRole } from "@/constants/customerNav";

const SEV_TONE = {
  critical: "text-rose-700 bg-rose-50 border-rose-200",
  high:     "text-amber-800 bg-amber-50 border-amber-200",
  medium:   "text-slate-800 bg-slate-50 border-slate-200",
  low:      "text-slate-700 bg-white border-slate-200",
};

const greetingFor = () => {
  const h = new Date().getHours();
  if (h < 5)  return "Good night";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const fmt = (n) => n == null ? "—" : Number(n).toLocaleString();
const fmtINR = (n) => n == null ? "₹0" :
  Number(n).toLocaleString("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 });
const fmtWhen = (t) => {
  if (!t) return "";
  const d = new Date(t);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h`;
  return `${Math.floor(mins / (60 * 24))}d`;
};

/* ================================================================ */
export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [askInput, setAskInput] = useState("");

  useEffect(() => {
    let cancel = false;
    // Load in parallel — never block on any single failing call.
    Promise.allSettled([
      api.get("/dashboard/executive"),
      api.get("/commercial/pipeline/overview"),
      api.get("/sales-desk/home?limit=6"),
    ]).then(([exec, pipe, sd]) => {
      if (cancel) return;
      if (exec.status === "fulfilled") setData(exec.value.data);
      if (pipe.status === "fulfilled") setPipeline(pipe.value.data);
      if (sd.status === "fulfilled")   setSales(sd.value.data);
      setLoading(false);
    });
    return () => { cancel = true; };
  }, []);

  const kpis = useMemo(() => data?.kpi || {}, [data?.kpi]);
  const attention = (data?.attention || []).filter((a) => (a.count || 0) > 0);
  const activity = data?.lifecycle_activity || {};
  const firstName = (user?.name || "there").split(" ")[0];
  const now = new Date().toLocaleString("en-GB",
    { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  const pulseTiles = useMemo(() => ([
    { label: "In use",           value: fmt(kpis.in_use),         onClick: () => navigate("/assets?lifecycle_state=in_use"),       testId: DASH.kpiInUse },
    { label: "Available",        value: fmt(kpis.available),      onClick: () => navigate("/assets?lifecycle_state=in_warehouse"), testId: DASH.kpiAvailable },
    { label: "In transit",       value: fmt(kpis.in_transit),     onClick: () => navigate("/shipments"),                            testId: DASH.kpiInTransit },
    { label: "Recovery",         value: fmt(kpis.recovery),       onClick: () => navigate("/pickups"),                              testId: "kpi-recovery" },
    { label: "Repair",           value: fmt(kpis.repair),         onClick: () => navigate("/service"),                              testId: DASH.kpiUnderRepair },
    { label: "Action required",  value: fmt(kpis.action_required), onClick: () => navigate("/approvals"),                            testId: "kpi-action",
      tone: (kpis.action_required || 0) > 0 ? "warn" : undefined },
  ]), [kpis, navigate]);

  const totalAttention = attention.reduce((n, a) => n + (a.count || 0), 0);

  // Priority actions synthesised from attention buckets + sales-desk
  // signals (drafts / awaiting response / accepted awaiting PO / PO
  // mismatch).  Only actionable items — no informational fillers.
  const importantActions = useMemo(() => {
    const rows = [];
    (attention || []).forEach((a) => rows.push({
      title:    a.label,
      context:  a.owner || "SUNTRONIX",
      severity: a.severity || "medium",
      count:    a.count,
      link:     a.link || "/exceptions",
      testId:   `imp-${a.kind}`,
    }));
    if (pipeline?.stages?.PO_MISMATCH?.count > 0) {
      rows.push({ title: "Customer PO mismatch",
        context: "Commercial", severity: "high",
        count:   pipeline.stages.PO_MISMATCH.count,
        link:    "/commercial/customer-po",
        testId:  "imp-po-mismatch" });
    }
    if ((sales?.awaiting_response || []).length > 0) {
      rows.push({ title: "Awaiting customer response",
        context: "Sales", severity: "medium",
        count:   sales.awaiting_response.length,
        link:    "/commercial/crm/quotations",
        testId:  "imp-await" });
    }
    if ((sales?.drafts || []).length > 0) {
      rows.push({ title: "Draft quotes pending review",
        context: "Sales", severity: "low",
        count:   sales.drafts.length,
        link:    "/commercial/crm/quotations",
        testId:  "imp-drafts" });
    }
    return rows.slice(0, 7);
  }, [attention, pipeline, sales]);

  const lifecycle = [
    { key: "PROCURE",      label: "Procure",   count: activity.ordered,   link: "/requests",  icon: Receipt },
    { key: "DEPLOY",       label: "Deploy",    count: activity.deployed,  link: "/shipments", icon: Rocket },
    { key: "MANAGE",       label: "Manage",    count: kpis.in_use,        link: "/assets?lifecycle_state=in_use", icon: ShieldCheck },
    { key: "SUPPORT",      label: "Support",   count: kpis.repair,        link: "/service",   icon: Wrench },
    { key: "RECOVER",      label: "Recover",   count: activity.recovered, link: "/pickups",   icon: Undo2 },
    { key: "REUSE",        label: "Reuse",     count: activity.reused,    link: "/warehouse", icon: Warehouse },
    { key: "REDEPLOY",     label: "Redeploy",  count: activity.received,  link: "/deploy",    icon: Truck },
    { key: "RECYCLE",      label: "Recycle",   count: activity.disposed,  link: "/itad",      icon: Recycle },
  ];

  const askSubmit = (e) => {
    e.preventDefault();
    if (!askInput.trim()) return;
    // Delegate to global Ask ONE — reuses ChatDrawerButton entry point.
    document.querySelector("[data-testid='chat-drawer-open']")?.click();
  };

  return (
    <div className="space-y-3 max-w-[1500px] mx-auto px-4 pt-3" data-testid={DASH.header || "dash-page"}>
      {/* =========================================================
        *  1) COMPACT IMAGE HERO STRIP (~120px) — greeting + brand
        *     Premium enterprise image + navy overlay. No weather,
        *     no celebrity quote (real content only).
        * ========================================================= */}
      <header
        data-testid="dash-header"
        className="relative overflow-hidden rounded-2xl text-white px-5 sm:px-7 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div aria-hidden className="absolute inset-0 bg-center bg-cover"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600)" }} />
        <div aria-hidden className="absolute inset-0"
          style={{ background: "linear-gradient(110deg, rgba(9,16,32,0.94) 0%, rgba(11,20,40,0.86) 50%, rgba(20,40,75,0.72) 100%)" }} />
        <div className="relative min-w-0">
          <div className="text-[10px] uppercase tracking-[0.16em] text-white/60">
            My ONE · Executive Command Centre
          </div>
          <h1 className="text-[22px] leading-tight font-heading font-semibold mt-0.5">
            <span className="font-normal text-white/70">{greetingFor()},</span>{" "}
            {firstName}
          </h1>
          <div className="text-[12px] text-white/70 mt-1 flex items-center gap-2 flex-wrap">
            <span>Here's what needs your attention across SUNTRONIX ONE.</span>
            <span className="hidden lg:inline text-white/30">·</span>
            <span className="hidden lg:inline text-[11px] font-medium" style={{ color: "#8fd6f5" }}>Technology. Managed around people.</span>
            <span className="hidden lg:inline text-white/30">·</span>
            <ChipStat dark label="Assets" value={fmt(data?.total_assets)} testId="hdr-chip-assets" />
            <ChipStat dark label="Countries" value={fmt(data?.countries)} testId="hdr-chip-countries" />
            <ChipStat dark label="Customers" value={fmt(data?.active_customers)}
              testId="hdr-chip-customers" fallback={fmt((sales?.recent_customers || []).length || null)} />
            <span className="text-white/50">· Updated {now}</span>
          </div>
        </div>
        <div className="relative flex items-center gap-1.5" data-testid="dash-header-actions">
          <HeaderBtn label="Create" icon={Plus} primary
            testId="dash-create-btn"
            onClick={() => document.querySelector('[data-testid="one-bar-create"]')?.click()} />
          <HeaderBtn label="Sales Desk" icon={Sparkles} onHero
            testId="dash-sales-desk-btn"
            onClick={() => navigate("/sales-desk")} />
          <HeaderBtn label="Exceptions" icon={AlertTriangle} onHero
            testId="dash-exceptions-btn"
            onClick={() => navigate("/exceptions")} />
          <HeaderBtn label="Ask ONE" icon={MessageSquare} onHero
            testId="dash-ask-btn"
            onClick={() => document.querySelector("[data-testid='chat-drawer-open']")?.click()} />
        </div>
      </header>

      {/* =========================================================
        *  2) EXECUTIVE PULSE — 6 dense tiles
        * ========================================================= */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2"
        data-testid="dash-estate">
        {pulseTiles.map((t) => <PulseTile key={t.label} {...t} loading={loading} />)}
      </div>

      {/* =========================================================
        *  3) MAIN GRID — 8 / 4 columns on desktop
        * ========================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* LEFT — 8 COLUMNS */}
        <div className="lg:col-span-8 space-y-3 min-w-0">

          {/* ATTENTION CENTRE */}
          <Panel title="Needs your attention"
            subtitle={totalAttention === 0 ? "Everything is clean" : "Actionable items across the platform"}
            testId="dash-attention"
            countBadge={totalAttention > 0 ? totalAttention : null}
            action={attention.length > 0 && (
              <PanelLink onClick={() => navigate("/exceptions")}>
                Exception centre
              </PanelLink>
            )}>
            {loading ? (
              <div className="p-3 space-y-1.5">
                {[0,1,2].map((i) => <div key={i} className="st-skeleton h-8 rounded" />)}
              </div>
            ) : attention.length === 0 ? (
              <div className="px-3 py-2.5 flex items-center gap-2 text-[13px]">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={1.8} />
                <span className="text-[color:var(--st-text)] font-medium">All clear.</span>
                <span className="text-[color:var(--st-text-muted)]">No critical action.</span>
              </div>
            ) : (
              <div className="grid grid-cols-[80px_1fr_80px_60px_30px] px-3 py-1.5 text-[10px] uppercase tracking-wider text-[color:var(--st-text-subtle)] border-b border-[color:var(--st-border)]">
                <div>Severity</div><div>Issue</div><div>Owner</div>
                <div className="text-right">Count</div><div />
              </div>
            )}
            {!loading && attention.length > 0 && (
              <ul className="divide-y divide-[color:var(--st-border)]">
                {attention.map((a) => (
                  <li key={a.kind + a.label}>
                    <button
                      onClick={() => navigate(a.link || "/exceptions")}
                      data-testid={`attn-${a.kind}`}
                      className="w-full text-left px-3 py-2 grid grid-cols-[80px_1fr_80px_60px_30px] items-center gap-2 hover:bg-[color:var(--st-surface-alt)]">
                      <span className={
                        "inline-flex text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-medium w-fit " +
                        (SEV_TONE[a.severity] || SEV_TONE.medium)
                      }>{a.severity || "medium"}</span>
                      <span className="text-[13px] text-[color:var(--st-text)] truncate">
                        {a.label}
                      </span>
                      <span className="text-[11px] text-[color:var(--st-text-muted)] truncate">
                        {a.owner || "SUNTRONIX"}
                      </span>
                      <span className="tabular-nums font-mono text-[14px] font-semibold text-right text-[color:var(--st-text)]">
                        {a.count}
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 text-[color:var(--st-text-subtle)]" strokeWidth={1.8} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {/* TWO-COLUMN: Commercial Pulse + Operations Pulse */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Panel title="Commercial pulse" subtitle="Live pipeline · not revenue"
              testId="dash-commercial"
              action={<PanelLink onClick={() => navigate("/sales-desk")}>Open Sales Desk</PanelLink>}>
              <div className="grid grid-cols-2 gap-y-2 gap-x-3 p-3 text-[11px]">
                <PulseStat testId="cp-draft"      label="Quotes · Draft"
                  value={pipeline?.stages?.QUOTATIONS_CREATED?.count}
                  onClick={() => navigate("/commercial/crm/quotations?status=DRAFT")} />
                <PulseStat testId="cp-submitted"  label="Quotes · Submitted"
                  value={pipeline?.stages?.QUOTATIONS_SUBMITTED?.count}
                  onClick={() => navigate("/commercial/crm/quotations?status=SENT")} />
                <PulseStat testId="cp-accepted"   label="Quotes · Accepted"
                  tone="ok"
                  value={pipeline?.stages?.QUOTATIONS_ACCEPTED?.count}
                  onClick={() => navigate("/commercial/crm/quotations?status=ACCEPTED")} />
                <PulseStat testId="cp-cpo"           label="Customer POs"
                  value={pipeline?.stages?.CUSTOMER_PO_RECEIVED?.count}
                  onClick={() => navigate("/commercial/customer-po")} />
                <PulseStat testId="cp-orders"       label="Orders confirmed"
                  tone="ok"
                  value={pipeline?.stages?.ORDERS_CREATED?.count}
                  onClick={() => navigate("/commercial/one-orders")} />
                <PulseStat testId="cp-invoices"    label="Invoices pending"
                  value={data?.commercial?.invoices_pending}
                  onClick={() => navigate("/invoices")} />
              </div>
              <div className="px-3 py-2 border-t border-[color:var(--st-border)] grid grid-cols-2 gap-2 text-[11px]">
                <ValStat label="Quoted value" hint="NOT revenue"
                  value={fmtINR(pipeline?.commercial_values?.total_quoted_value)} />
                <ValStat label="Accepted value" hint="NOT revenue"
                  value={fmtINR(pipeline?.commercial_values?.accepted_quoted_value)} />
                <ValStat label="Customer PO"
                  value={fmtINR(pipeline?.commercial_values?.customer_po_value)} tone="ok" />
                <ValStat label="Confirmed order"
                  value={fmtINR(pipeline?.commercial_values?.confirmed_order_value)} tone="ok" />
              </div>
            </Panel>

            <Panel title="Operations pulse" subtitle="Current work in motion"
              testId="dash-operations"
              action={<PanelLink onClick={() => navigate("/deployment-centre")}>Control tower</PanelLink>}>
              <div className="grid grid-cols-2 gap-y-2 gap-x-3 p-3 text-[11px]">
                <PulseStat testId="op-deploy"       label="Deployments today"
                  value={activity.deployed}   onClick={() => navigate("/shipments")} />
                <PulseStat testId="op-transit"      label="Shipments in transit"
                  value={kpis.in_transit}     onClick={() => navigate("/shipments")} />
                <PulseStat testId="op-recovery"    label="Recoveries open"
                  value={kpis.recovery}       onClick={() => navigate("/pickups")} />
                <PulseStat testId="op-repair"        label="Repairs open"
                  value={kpis.repair}           onClick={() => navigate("/service")} />
                <PulseStat testId="op-support"     label="Support cases"
                  value={data?.operations?.support_cases}
                  onClick={() => navigate("/support")} />
                <PulseStat testId="op-warehouse" label="Warehouse actions"
                  value={activity.received}   onClick={() => navigate("/warehouse")} />
              </div>
            </Panel>
          </div>

          {/* TECHNOLOGY LIFECYCLE */}
          <Panel title="Technology lifecycle"
            subtitle="Movement across the last 90 days"
            testId="dash-lifecycle">
            <div className="grid grid-cols-4 md:grid-cols-8 divide-x divide-[color:var(--st-border)]">
              {lifecycle.map((s) => {
                const Icon = s.icon;
                return (
                  <button key={s.key} onClick={() => navigate(s.link)}
                    data-testid={`life-${s.key.toLowerCase()}`}
                    className="px-2.5 py-2 text-left hover:bg-[color:var(--st-surface-alt)]">
                    <Icon className="h-3.5 w-3.5 text-[color:var(--st-text-muted)]" strokeWidth={1.6} />
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--st-text-subtle)] mt-0.5">
                      {s.label}
                    </div>
                    <div className="tabular-nums text-[16px] font-heading font-semibold text-[color:var(--st-text)] leading-tight mt-0.5">
                      {fmt(s.count)}
                    </div>
                  </button>
                );
              })}
            </div>
          </Panel>

          {/* CUSTOMER PULSE */}
          <Panel title="Customer pulse" subtitle="Tenant snapshot"
            testId="dash-customer-pulse"
            action={<PanelLink onClick={() => navigate("/companies")}>All customers</PanelLink>}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-2 gap-x-3 p-3 text-[11px]">
              <PulseStat testId="cust-active" label="Active customers"
                value={(sales?.recent_customers || []).length || null}
                onClick={() => navigate("/companies")} />
              <PulseStat testId="cust-open-orders" label="Open orders"
                value={pipeline?.stages?.ORDERS_CREATED?.count}
                onClick={() => navigate("/commercial/one-orders")} />
              <PulseStat testId="cust-exceptions" label="With exceptions"
                value={totalAttention}
                tone={totalAttention > 0 ? "warn" : undefined}
                onClick={() => navigate("/exceptions")} />
              <PulseStat testId="cust-recovery" label="With active recovery"
                value={kpis.recovery}
                onClick={() => navigate("/pickups")} />
            </div>
          </Panel>
        </div>

        {/* RIGHT — 4 COLUMNS */}
        <div className="lg:col-span-4 space-y-3 min-w-0">
          {/* IMPORTANT ACTIONS */}
          <Panel title="Important actions" subtitle="Focus on what moves next"
            testId="dash-important"
            action={importantActions.length > 0 && (
              <PanelLink onClick={() => navigate("/exceptions")}>Everything</PanelLink>
            )}>
            {importantActions.length === 0 ? (
              <div className="px-3 py-2.5 text-[12px] text-[color:var(--st-text-muted)] italic">
                No actions requiring your attention right now.
              </div>
            ) : (
              <ul className="divide-y divide-[color:var(--st-border)]">
                {importantActions.map((r, i) => (
                  <li key={i}>
                    <button onClick={() => navigate(r.link)}
                      data-testid={r.testId}
                      className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[color:var(--st-surface-alt)]">
                      <span className={
                        "inline-flex text-[9px] uppercase tracking-wider px-1 py-0.5 rounded border font-semibold shrink-0 " +
                        (SEV_TONE[r.severity] || SEV_TONE.medium)
                      }>{r.severity}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] text-[color:var(--st-text)] font-medium truncate">
                          {r.title}
                        </div>
                        <div className="text-[10px] text-[color:var(--st-text-muted)] truncate">
                          {r.context}
                        </div>
                      </div>
                      <span className="tabular-nums font-mono text-[13px] font-semibold text-[color:var(--st-text)] shrink-0">
                        {r.count}
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 text-[color:var(--st-text-subtle)] shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {/* QUICK ACTIONS */}
          <Panel title="Quick actions" testId="dash-quick">
            <div className="p-2 grid grid-cols-1 gap-1.5">
              <QA testId="qa-quotations" icon={Receipt} label="Quotations"
                onClick={() => navigate("/commercial/crm/quotations")} />
              <QA testId="qa-new-quote"   primary
                icon={Plus}    label="New Quotation"
                onClick={() => navigate("/commercial/crm/quotations?new=1")} />
              <div className="grid grid-cols-2 gap-1.5">
                <QA testId="qa-add-customer" icon={Users}    label="Customer"
                  onClick={() => navigate("/companies")} />
                <QA testId="qa-add-product"  icon={Package}  label="Product"
                  onClick={() => navigate("/technology/product-master")} />
                <QA testId="qa-cpo"          icon={Receipt}  label="Customer PO"
                  onClick={() => navigate("/commercial/customer-po?new=1")} />
                <QA testId="qa-order"        icon={ClipboardList} label="Order"
                  onClick={() => navigate("/commercial/one-orders")} />
              </div>
              {user && !isCustomerRole(user.role) && !String(user.role || "").startsWith("supplier") && (
                <QA testId="qa-direct-sale-label" icon={Gift} label="Walk-in / Gift label"
                  onClick={() => navigate("/admin/welcome-labels?tab=direct-sales")} />
              )}
              <QA testId="qa-social-media" icon={MessageSquare} label="Social Media"
                onClick={() => navigate("/marketing/social-media")} />
              <QA testId="qa-search-asset" icon={SearchIcon} label="Search Asset"
                onClick={() => navigate("/assets")} />
            </div>
          </Panel>

          {/* ASK ONE */}
          <Panel title="Ask ONE" subtitle="Query your operations conversationally"
            testId="dash-ask">
            <form onSubmit={askSubmit} className="p-2.5 space-y-2">
              <div className="flex items-center gap-1.5 border border-[color:var(--st-border)] rounded-md px-2.5 h-9 focus-within:border-emerald-400">
                <MessageSquare className="h-3.5 w-3.5 text-[color:var(--st-text-muted)]" />
                <input value={askInput}
                  onChange={(e) => setAskInput(e.target.value)}
                  placeholder='e.g. "Which POs need review?"'
                  data-testid="dash-ask-input"
                  className="flex-1 bg-transparent outline-none text-[12px]" />
                <button type="submit" data-testid="dash-ask-send"
                  className="text-[11px] text-emerald-700 font-medium hover:underline">
                  Ask →
                </button>
              </div>
              <div className="text-[10px] text-[color:var(--st-text-subtle)] flex flex-wrap gap-1">
                {["Which orders need attention?",
                     "Show delayed deployments",
                     "POs not matching quotes"].map((s) => (
                  <button key={s} type="button"
                    onClick={() => setAskInput(s)}
                    className="px-1.5 py-0.5 rounded border border-[color:var(--st-border)] hover:bg-[color:var(--st-surface-alt)]">
                    {s}
                  </button>
                ))}
              </div>
            </form>
          </Panel>

          {/* PLATFORM HEALTH */}
          <Panel title="Platform health" subtitle="Connector signal"
            testId="dash-platform-health"
            action={<PanelLink onClick={() => navigate("/admin/integration-health")}>Details</PanelLink>}>
            <ul className="divide-y divide-[color:var(--st-border)] text-[12px]">
              {[
                { name: "Courier",       status: "HEALTHY" },
                { name: "Email · Resend", status: "HEALTHY" },
                { name: "Object storage", status: "HEALTHY" },
              ].map((c) => (
                <li key={c.name} className="px-3 py-1.5 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <span className="text-[color:var(--st-text)] flex-1 truncate">{c.name}</span>
                  <span className="text-[10px] uppercase tracking-wider text-emerald-700">
                    {c.status}
                  </span>
                </li>
              ))}
            </ul>
          </Panel>

          {/* RECENT ACTIVITY */}
          <Panel title="Recent activity" subtitle="Latest ONE events"
            testId="dash-recent"
            action={<PanelLink onClick={() => navigate("/audit")}>Full timeline</PanelLink>}>
            <ul className="divide-y divide-[color:var(--st-border)] text-[11px]">
              {((data?.recent || []).slice(0, 6).length > 0
                ? (data?.recent || []).slice(0, 6)
                : ((sales?.recent_quotations || []).slice(0, 4).map((q) => ({
                    label: `Quote ${q.code} · ${q.customer_name || ""}`,
                    time:  q.updated_at || q.created_at,
                    icon:  Receipt,
                  }))))
                .map((r, i) => {
                  const Icon = r.icon || Activity;
                  return (
                    <li key={i} className="px-3 py-1.5 flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5 text-[color:var(--st-text-muted)] shrink-0" strokeWidth={1.6} />
                      <span className="text-[color:var(--st-text)] truncate flex-1">
                        {r.label}
                      </span>
                      {r.time && (
                        <span className="text-[10px] text-[color:var(--st-text-subtle)] shrink-0 tabular-nums">
                          {fmtWhen(r.time)}
                        </span>
                      )}
                    </li>
                  );
              })}
            </ul>
          </Panel>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-[10px] text-[color:var(--st-text-subtle)] pt-2 border-t border-[color:var(--st-border)]">
        <span className="font-heading text-[color:var(--st-text-muted)] font-medium tracking-tight">
          SUNTRONIX ONE · Global Technology Lifecycle Platform
        </span>
      </div>
    </div>
  );
}

/* ================================================================ */
function ChipStat({ label, value, testId, fallback, dark }) {
  const v = (value && value !== "—") ? value : (fallback || "—");
  return (
    <span data-testid={testId}
      className={"inline-flex items-baseline gap-1 px-1.5 py-0.5 rounded border " +
        (dark ? "border-white/20 bg-white/10" : "border-[color:var(--st-border)] bg-[color:var(--st-surface)]")}>
      <span className={"text-[9px] uppercase tracking-wider " + (dark ? "text-white/60" : "text-[color:var(--st-text-subtle)]")}>
        {label}
      </span>
      <span className={"tabular-nums text-[11px] font-semibold " + (dark ? "text-white" : "text-[color:var(--st-text)]")}>
        {v}
      </span>
    </span>
  );
}

function PulseTile({ label, value, onClick, testId, tone, loading }) {
  const warn = tone === "warn";
  return (
    <button onClick={onClick} data-testid={testId}
      className={
        "text-left rounded-md border px-3 py-2 transition-colors hover:bg-[color:var(--st-surface-alt)] group " +
        (warn
          ? "border-amber-300 bg-amber-50/60 hover:bg-amber-50"
          : "border-[color:var(--st-border)] bg-[color:var(--st-surface)]")
      }>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)]">
        {label}
      </div>
      {loading ? (
        <div className="st-skeleton h-7 w-16 mt-1 rounded" />
      ) : (
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className={"tabular-nums text-[22px] leading-tight font-heading font-semibold " +
            (warn ? "text-amber-800" : "text-[color:var(--st-text)]")}>
            {value}
          </span>
          <span className="text-[10px] text-emerald-700 hidden lg:inline opacity-0 group-hover:opacity-100 transition-opacity">
            View →
          </span>
        </div>
      )}
    </button>
  );
}

function PulseStat({ label, value, onClick, testId, tone }) {
  const ok  = tone === "ok";
  const warn = tone === "warn";
  return (
    <button onClick={onClick} data-testid={testId}
      className="text-left group">
      <div className="text-[10px] uppercase tracking-wider text-[color:var(--st-text-subtle)] flex items-center gap-1 group-hover:text-[color:var(--st-text-muted)]">
        {label}
      </div>
      <div className={"tabular-nums text-[18px] font-heading font-semibold leading-tight mt-0.5 " +
        (ok ? "text-emerald-700" : warn ? "text-amber-800" : "text-[color:var(--st-text)]")}>
        {fmt(value)}
      </div>
    </button>
  );
}

function ValStat({ label, value, hint, tone }) {
  const ok = tone === "ok";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">
        {label}
      </div>
      <div className={"tabular-nums font-mono text-[13px] font-semibold mt-0.5 " +
        (ok ? "text-emerald-700" : "text-[color:var(--st-text)]")}>
        {value}
      </div>
      {hint && <div className="text-[9px] italic text-[color:var(--st-text-subtle)]">{hint}</div>}
    </div>
  );
}

function Panel({ title, subtitle, action, countBadge, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface)] overflow-hidden">
      {(title || action) && (
        <header className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[color:var(--st-border)]">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-[12px] font-semibold text-[color:var(--st-text)] tracking-tight">
                {title}
              </h2>
              {countBadge != null && (
                <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                  {countBadge}
                </span>
              )}
            </div>
            {subtitle && (
              <div className="text-[10px] text-[color:var(--st-text-muted)]">{subtitle}</div>
            )}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

function PanelLink({ onClick, children }) {
  return (
    <button onClick={onClick}
      className="text-[11px] text-emerald-700 hover:underline flex items-center gap-0.5">
      {children} <ChevronRight className="h-3 w-3" />
    </button>
  );
}

function HeaderBtn({ label, icon: Icon, onClick, primary, onHero, testId }) {
  const base = "h-8 px-3 rounded-md text-[12px] font-medium flex items-center gap-1.5 transition-colors";
  const cls = primary
    ? "bg-white text-[#0b1220] hover:bg-white/90"
    : onHero
      ? "border border-white/25 text-white hover:bg-white/10"
      : "border border-[color:var(--st-border)] text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]";
  return (
    <button onClick={onClick} data-testid={testId} className={base + " " + cls}>
      <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
      <span className="hidden md:inline">{label}</span>
    </button>
  );
}

function QA({ icon: Icon, label, onClick, primary, testId }) {
  const base = "h-8 rounded-md text-[12px] font-medium flex items-center gap-1.5 px-3 justify-start transition-colors";
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={base + " " + (primary
        ? "bg-emerald-700 hover:bg-emerald-800 text-white"
        : "border border-[color:var(--st-border)] text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]")}>
      <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
      <span className="truncate">{label}</span>
    </button>
  );
}
